import React, { useState, useEffect } from "react";
import {
  FiUpload,
  FiCheckCircle,
  FiAlertCircle,
  FiCreditCard,
  FiInfo,
  FiMapPin,
  FiCopy,
} from "react-icons/fi";
import styles from "./DonationForm.module.scss";

// Opsi jumlah donasi
const donationPresets = [25000, 50000, 100000, 250000];

// Helper untuk format mata uang
const formatCurrency = (number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(number);
};

const DonationForm = () => {
  // State untuk data form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(donationPresets[1]); // Default 50rb
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [message, setMessage] = useState("");
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // State untuk UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Efek untuk preview gambar
  useEffect(() => {
    if (!proof) {
      setProofPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(proof);
    setProofPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [proof]);

  // Handler input file
  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!allowedTypes.includes(file.type)) {
        setError("Format file tidak didukung. Gunakan JPG, PNG, atau WebP.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Ukuran file terlalu besar. Maksimal 5MB.");
        return;
      }
      setError(null);
      setProof(file);
    }
  };

  // Handler preset
  const handlePresetClick = (presetAmount) => {
    setAmount(presetAmount);
    setIsCustomAmount(false);
  };

  // Handler custom amount
  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setAmount(value ? parseInt(value, 10) : 0);
    setIsCustomAmount(true);
  };

  // Fitur Copy Rekening
  const handleCopyRekening = () => {
    navigator.clipboard.writeText("1234567890"); // Ganti dengan No Rek Asli
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Handler submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    if (!name || !email || !amount || !proof) {
      setError("Mohon lengkapi semua field yang wajib diisi.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("total", amount);
    formData.append("message", message);
    formData.append("file", proof);

    try {
      const response = await fetch(
        "https://api.surosowancyber.or.id/donation/create",
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Terjadi kesalahan saat mengirim donasi.",
        );
      }

      setSuccess(true);
      setName("");
      setEmail("");
      setAmount(donationPresets[1]);
      setMessage("");
      setProof(null);
      setProofPreview(null);
      setIsCustomAmount(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.donationSection}>
      <div className={styles.container}>
        <div className={styles.splitCard}>
          {/* KOLOM KIRI: INFORMASI (INFO SIDE) */}
          <div className={styles.infoSide}>
            <div className={styles.infoHeader}>
              <h3>Surosowan Siber</h3>
              <span className={styles.badge}>Non-Profit Organization</span>
            </div>

            <div className={styles.infoDescription}>
              <p>
                <strong>Mencetak Jawara Digital Banten.</strong>
                <br />
                Kami percaya teknologi milik semua orang. Dukungan Anda menjadi
                "bahan bakar" utama bagi kami untuk terus menyelenggarakan:
              </p>

              {/* Highlight Program Unggulan */}
              <div
                style={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  paddingLeft: "0.5rem",
                  borderLeft: "3px solid #38bdf8",
                }}>
                <p style={{ marginBottom: "0.5rem", color: "white" }}>
                  🚀 <strong>Surosowan Cyber Academy</strong>
                </p>
                <p style={{ fontSize: "0.9em", color: "#cbd5e1" }}>
                  Program inkubasi tahunan intensif untuk melahirkan talenta
                  keamanan siber dan programmer handal secara gratis.
                </p>
              </div>

              <p>
                Serta kegiatan rutin mingguan seperti{" "}
                <strong>Workshop, Seminar Literasi Digital,</strong> dan{" "}
                <strong>Pelatihan Teknis</strong> untuk pelajar dan masyarakat
                umum.
              </p>

              <div className={styles.donationUsage}>
                <h4>
                  <FiInfo className={styles.iconInline} /> Alokasi Donasi Anda:
                </h4>
                <ul>
                  <li>1. Beasiswa Peserta Cyber Academy</li>
                  <li>2. Operasional Seminar & Workshop</li>
                  <li>3. Sewa Server Lab & Infrastruktur</li>
                </ul>
              </div>
            </div>

            <div className={styles.bankCard}>
              <div className={styles.bankLabel}>
                <FiCreditCard /> Rekening Resmi Yayasan
              </div>
              <div className={styles.bankName}>BCA DIGITAL</div>
              <div className={styles.accountNumberWrapper}>
                <span className={styles.accountNumber}>001-119-980-530</span>
                <button
                  type='button'
                  onClick={handleCopyRekening}
                  className={styles.copyButton}
                  title='Salin Nomor Rekening'>
                  {copySuccess ? <FiCheckCircle color='#38bdf8' /> : <FiCopy />}
                </button>
              </div>
              <div className={styles.accountOwner}>
                a.n Bambang Purbo Asmoro
              </div>
            </div>

            <div className={styles.contactInfo}>
              <p>
                <FiMapPin /> Serang, Banten, Indonesia
              </p>
            </div>
          </div>

          {/* KOLOM KANAN: FORMULIR (FORM SIDE) */}
          <div className={styles.formSide}>
            <div className={styles.formHeader}>
              <h2 className={styles.title}>Konfirmasi Dukungan</h2>
              <p className={styles.subtitle}>
                Terima kasih telah mengambil peran. Mohon lengkapi data berikut
                agar kami dapat mencatat amanah Anda dengan baik dan transparan.
              </p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Input Nama & Email */}
              <div className={styles.gridTwoCols}>
                <div className={styles.inputGroup}>
                  <label htmlFor='name' className={styles.label}>
                    Nama Lengkap / Inisial *
                  </label>
                  <input
                    type='text'
                    name='name'
                    id='name'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={styles.input}
                    placeholder='Contoh: Abdullah atau Hamba Allah'
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor='email' className={styles.label}>
                    Email Aktif *
                  </label>
                  <input
                    type='email'
                    name='email'
                    id='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.input}
                    placeholder='Untuk pengiriman bukti donasi'
                  />
                </div>
              </div>

              {/* Pilihan Jumlah */}
              <div className={styles.amountSection}>
                <label className={styles.label}>Nominal Donasi *</label>
                <div className={styles.presetGrid}>
                  {donationPresets.map((preset) => (
                    <button
                      type='button'
                      key={preset}
                      onClick={() => handlePresetClick(preset)}
                      className={`${styles.presetButton} ${!isCustomAmount && amount === preset ? styles.active : ""}`}>
                      {formatCurrency(preset)}
                    </button>
                  ))}
                </div>
                <div className={styles.customAmountWrapper}>
                  <span className={styles.currencySymbol}>Rp</span>
                  <input
                    type='text'
                    value={isCustomAmount ? amount : ""}
                    onChange={handleCustomAmountChange}
                    placeholder='Nominal lainnya (Min. 10.000)'
                    className={styles.inputCustom}
                  />
                </div>
              </div>

              {/* Input Pesan */}
              <div className={styles.inputGroup}>
                <label htmlFor='message' className={styles.label}>
                  Pesan Semangat / Doa (Opsional)
                </label>
                <textarea
                  name='message'
                  rows='2'
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className={styles.textarea}
                  placeholder='Contoh: Semoga Surosowan Siber makin maju dan bermanfaat buat Banten.'
                />
              </div>

              {/* Upload Bukti */}
              <div className={styles.uploadSection}>
                <label className={styles.label}>Unggah Bukti Transfer *</label>
                <div className={styles.fileDropzone}>
                  {proofPreview ? (
                    <div className={styles.previewContainer}>
                      <img
                        src={proofPreview}
                        alt='Preview'
                        className={styles.proofPreview}
                      />
                      <button
                        type='button'
                        onClick={() => setProof(null)}
                        className={styles.removeBtn}>
                        Ganti File
                      </button>
                    </div>
                  ) : (
                    <label htmlFor='file-upload' className={styles.uploadLabel}>
                      <FiUpload className={styles.uploadIcon} />
                      <span>Klik untuk unggah tangkapan layar</span>
                      <small>Format: JPG, PNG (Maks. 5MB)</small>
                      <input
                        id='file-upload'
                        type='file'
                        className={styles.srOnly}
                        onChange={handleFileChange}
                        accept='image/*'
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Tombol & Alerts */}
              <div className={styles.submitWrapper}>
                {error && (
                  <div className={`${styles.alert} ${styles.error}`}>
                    <FiAlertCircle /> {error}
                  </div>
                )}
                {success && (
                  <div className={`${styles.alert} ${styles.success}`}>
                    <FiCheckCircle /> Donasi tercatat! Terima kasih orang baik.
                  </div>
                )}

                <button
                  type='submit'
                  disabled={loading || success}
                  className={styles.submitButton}>
                  {loading
                    ? "Sedang Mengirim..."
                    : `Kirim Konfirmasi ${formatCurrency(amount)}`}
                </button>
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: "#64748b",
                    marginTop: "0.5rem",
                  }}>
                  <FiInfo style={{ display: "inline", marginRight: "4px" }} />
                  Data Anda aman dan tidak akan dipublikasikan tanpa izin.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationForm;
